import React, { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import Modal from './Modal';
import { bulkUpload } from '../../services/api';

/**
 * BulkUpload modal – 3-step: Upload → Preview → Result
 *
 * Props:
 *   open        {bool}
 *   onClose     {fn}
 *   onSuccess   {fn}
 *   title       {string}
 *   uploadUrl   {string}   – e.g. '/customers/bulk'
 *   templateUrl {string}   – e.g. '/customers/template'
 *   columns     {[{key, label, required}]}
 *   queryParams {object}   – e.g. { type: 'invoice' }
 */
export default function BulkUpload({ open, onClose, onSuccess, title, uploadUrl, templateUrl, columns = [], queryParams = {} }) {
  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=result
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  const reset = () => {
    setStep(1); setFile(null); setRows([]); setParseError(''); setResult(null);
  };

  const handleClose = () => { reset(); onClose(); };

  // xlsx files are sent to the server for parsing (no client-side preview)
  // CSV files are previewed client-side using papaparse
  const handleFile = useCallback((f) => {
    if (!f) return;
    setParseError('');
    setFile(f);
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext === 'xlsx' || ext === 'xls') {
      // Skip client-side parse — server handles xlsx; show a ready-to-upload step
      setRows([]);
      setStep(2);
    } else {
      Papa.parse(f, {
        header: true,
        skipEmptyLines: true,
        transformHeader: h => h.trim(),
        transform: v => typeof v === 'string' ? v.trim() : v,
        complete: (res) => {
          if (!res.data.length) { setParseError('File appears to be empty.'); return; }
          setRows(res.data); setStep(2);
        },
        error: (err) => setParseError('Failed to parse CSV: ' + err.message),
      });
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await bulkUpload(uploadUrl, file, queryParams);
      setResult(data); setStep(3);
      if (data.created > 0 && onSuccess) onSuccess();
    } catch (err) {
      setResult({
        success: false,
        message: err.response?.data?.message || 'Upload failed.',
        created: 0,
        errors: [],
      });
      setStep(3);
    } finally { setUploading(false); }
  };

  const downloadTemplate = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(queryParams).toString();
    const url = `/api${templateUrl}${params ? '?' + params : ''}`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${title.replace(/\s+/g, '_').toLowerCase()}_template.csv`;
        a.click();
      });
  };

  const downloadErrors = () => {
    if (!result?.errors?.length) return;
    const csv = 'Row,Message\n' + result.errors.map(e => `${e.row},"${e.message}"`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'upload_errors.csv';
    a.click();
  };

  // Determine display columns: use provided or derive from first row
  const displayCols = columns.length
    ? columns
    : rows.length > 0 ? Object.keys(rows[0]).map(k => ({ key: k, label: k })) : [];

  const previewRows = rows.slice(0, 5);

  const footer = (() => {
    if (step === 1) return (
      <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
    );
    if (step === 2) return (
      <>
        <button className="btn btn-secondary" onClick={() => { setStep(1); setRows([]); setFile(null); }}>Back</button>
        <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
          {uploading
            ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Uploading…</>
            : rows.length ? `Upload ${rows.length} rows` : 'Upload File'}
        </button>
      </>
    );
    return (
      <>
        {result?.errors?.length > 0 && (
          <button className="btn btn-secondary" onClick={downloadErrors}>Download Errors</button>
        )}
        <button className="btn btn-primary" onClick={handleClose}>Done</button>
      </>
    );
  })();

  return (
    <Modal open={open} onClose={handleClose} title={title} size="lg" footer={footer}>
      {/* Step indicators */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--border)' }}>
        {['Upload File', 'Preview', 'Result'].map((label, idx) => (
          <div key={idx} style={{
            padding: '8px 20px',
            fontSize: '0.82rem',
            fontWeight: step === idx + 1 ? 700 : 400,
            color: step === idx + 1 ? 'var(--primary)' : step > idx + 1 ? 'var(--success)' : 'var(--text-muted)',
            borderBottom: step === idx + 1 ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -2,
          }}>
            <span style={{ marginRight: 6, fontWeight: 700 }}>{idx + 1}.</span>{label}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div>
          <div
            style={{
              border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: 12,
              padding: '40px 24px',
              textAlign: 'center',
              background: dragOver ? 'var(--primary-lighter)' : 'var(--surface-2)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <svg width="40" height="40" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" viewBox="0 0 24 24" style={{ marginBottom: 12 }}>
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
              Drop a CSV or Excel file here, or click to browse
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Supported formats: .csv, .xlsx · Max size: 5 MB
            </div>
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])} />
          </div>
          {parseError && <div className="alert alert-error" style={{ marginTop: 12 }}>{parseError}</div>}
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--surface-2)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Need a template? Download our pre-formatted CSV file.
            </span>
            <a href="#template" onClick={downloadTemplate}
              style={{ fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download Template
            </a>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && (
        <div>
          {rows.length > 0 ? (
            <>
              <div style={{ marginBottom: 12, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <strong>{rows.length}</strong> rows detected in <strong>{file?.name}</strong>
                {rows.length > 5 && <span style={{ color: 'var(--text-muted)' }}> (showing first 5)</span>}
              </div>
              <div className="table-container" style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 280, overflow: 'auto' }}>
                <table className="table" style={{ marginBottom: 0, fontSize: '0.8rem' }}>
                  <thead>
                    <tr>
                      {displayCols.map(col => (
                        <th key={col.key} style={{ whiteSpace: 'nowrap' }}>
                          {col.label}{col.required ? <span style={{ color: 'var(--error)' }}>*</span> : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i}>
                        {displayCols.map(col => (
                          <td key={col.key} style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row[col.key] !== undefined && row[col.key] !== '' ? row[col.key] : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <svg width="48" height="48" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" viewBox="0 0 24 24" style={{ marginBottom: 12 }}>
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{file?.name}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Excel file ready — click Upload to process on the server.
              </div>
            </div>
          )}
          {parseError && <div className="alert alert-error" style={{ marginTop: 12 }}>{parseError}</div>}
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && result && (
        <div>
          <div style={{ textAlign: 'center', padding: '20px 0', marginBottom: 16 }}>
            {result.created > 0 ? (
              <div style={{ color: 'var(--success)', marginBottom: 8 }}>
                <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
            ) : (
              <div style={{ color: 'var(--error)', marginBottom: 8 }}>
                <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
            )}
            <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 }}>
              {result.created} record{result.created !== 1 ? 's' : ''} created
            </div>
            {result.errors?.length > 0 && (
              <div style={{ color: 'var(--error)', fontSize: '0.875rem' }}>
                {result.errors.length} row{result.errors.length !== 1 ? 's' : ''} had errors
              </div>
            )}
          </div>

          {result.errors?.length > 0 && (
            <div style={{ border: '1px solid var(--error-light, #fecaca)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: 'var(--error-lighter, #fef2f2)', padding: '8px 12px', fontWeight: 600, fontSize: '0.83rem', color: 'var(--error)' }}>
                Errors ({result.errors.length})
              </div>
              <div style={{ maxHeight: 180, overflow: 'auto' }}>
                {result.errors.map((err, i) => (
                  <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: '0.8rem' }}>
                    <span style={{ fontWeight: 600 }}>Row {err.row}:</span>{' '}
                    <span style={{ color: 'var(--text-secondary)' }}>{err.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
